import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import {
  useGetResourcePagesQuery,
  useGetResourceItemFileUrlMutation,
} from '../app/api';
import { RichText } from './richtext';
import c2pCentralLoginLink from '../components/c2pCentralLink';

/* Resource Library — the internal reference pages, employee read view.
 *
 * One page per URL (/resources/<slug>), keyed on the slug, with the pages as
 * children of the sidebar's "Resource Library" group — the same shape the
 * Marketing library uses next to it. The nav group is built from this same
 * endpoint in Root.jsx, so adding or renaming a page in the Marketing admin page
 * moves both together.
 *
 * TWO LEVELS OF CONTENT: a page holds a flat list of items. Each item shows a
 * title, a description and one thing to open. No in-page groups — these are
 * lists of links and files, and the marketing library's middle level would be a
 * level nobody filled in. (Pages can sit inside sidebar FOLDERS, but that is
 * navigation and lives in Root.jsx; this view renders one page either way.)
 *
 * WHICH PAGES ARRIVE HERE IS THE SERVER'S DECISION, and it depends on who is
 * asking: every employee gets the `published` pages, and an employee holding the
 * BETA role also gets the ones staged for early access. So this component never
 * filters by audience — it renders what it is given. A page admitted only by the
 * beta flag carries `beta_preview`, and that is the one thing worth surfacing:
 * the reader should know the page is pre-release and that a colleague may not be
 * able to open it yet.
 *
 * FOUR KINDS, AND THE PAGE JUST READS `kind` — the server derives it (see
 * routers/resource_library.py), so nothing here has to guess from a URL or a
 * file extension:
 *
 *   LINK   an external site        → a real anchor. Opens in a new tab.
 *   EMBED  a YouTube/Vimeo video   → expands into an inline iframe player.
 *   VIDEO  an uploaded video       → expands into an inline <video>, whose src
 *                                    is a presigned URL minted on expand.
 *   FILE   an uploaded document    → a button that mints a presigned URL and
 *                                    opens it in a new tab.
 *
 * LINKS ARE REAL ANCHORS HERE, unlike on the marketing pages. There, every open
 * is routed through the API so the firm-engagement report can count it; there
 * is no such question about internal reference material, so middle-click, "copy
 * link address" and the browser's own status bar all work as they should.
 */

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  red:       '#c0392b',
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// A file's icon, picked from its extension so a deck, a sheet and a PDF are
// distinguishable at a glance in a long list.
const FILE_ICONS = {
  pdf: 'file-earmark-pdf', ppt: 'file-earmark-slides', pptx: 'file-earmark-slides',
  key: 'file-earmark-slides', doc: 'file-earmark-word', docx: 'file-earmark-word',
  xls: 'file-earmark-spreadsheet', xlsx: 'file-earmark-spreadsheet',
  xlsm: 'file-earmark-spreadsheet', csv: 'file-earmark-spreadsheet',
  txt: 'file-earmark-text', md: 'file-earmark-text', rtf: 'file-earmark-text',
  png: 'file-earmark-image', jpg: 'file-earmark-image', jpeg: 'file-earmark-image',
  gif: 'file-earmark-image', svg: 'file-earmark-image', webp: 'file-earmark-image',
  tif: 'file-earmark-image', tiff: 'file-earmark-image',
  zip: 'file-earmark-zip',
};

const fileIcon = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  return FILE_ICONS[ext] || 'file-earmark';
};

const cardStyle = {
  border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)',
  borderRadius: '12px', overflow: 'hidden', background: 'white',
};

const rowStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '0.75rem', width: '100%',
  textAlign: 'left', padding: '0.7rem 0.25rem', background: 'transparent',
  border: 'none', borderTop: `1px solid ${brand.softGray}`,
  textDecoration: 'none', cursor: 'pointer',
};

const KIND_ICONS = {
  LINK: 'box-arrow-up-right',
  EMBED: 'play-btn-fill',
  VIDEO: 'play-btn-fill',
};

/* A 16:9 box that keeps its shape at every width. `aspect-ratio` with
 * max-width:100% rather than the old padding-bottom trick, and the child is
 * absolutely positioned so an iframe with its own width attribute can't
 * override it. */
function PlayerFrame({ children }) {
  return (
    <div style={{
      position: 'relative', width: '100%', maxWidth: 880, aspectRatio: '16 / 9',
      background: '#000', borderRadius: 8, overflow: 'hidden',
      margin: '0.2rem 0 0.6rem',
    }}>
      <div style={{ position: 'absolute', inset: 0 }}>{children}</div>
    </div>
  );
}
PlayerFrame.propTypes = { children: PropTypes.node };
PlayerFrame.defaultProps = { children: null };

/* One row. Everything except a LINK is a button, because everything except a
 * LINK either expands in place or has no URL until it is asked for. */
function ResourceRow({ item, onError }) {
  const [getFileUrl] = useGetResourceItemFileUrlMutation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The presigned URL for an uploaded video, minted the first time the row is
  // expanded and then kept for the visit. Its TTL is hours, not the five
  // minutes a download link gets, precisely so it survives being watched.
  const [videoUrl, setVideoUrl] = useState(null);
  // Chrome won't play video/quicktime. Rather than guess from the content type,
  // the player reports its own failure and the row falls back to a download —
  // which is the only honest answer, since the file is fine and the browser
  // simply can't decode it.
  const [unplayable, setUnplayable] = useState(false);

  const { kind } = item;
  const isUpload = kind === 'FILE' || kind === 'VIDEO';
  // An upload shows its size and nothing else does. Filename, updated date and
  // the kind label ("External link", "Video") all used to sit here: on a page
  // of course links they were three lines of noise under every title, the kind
  // is already obvious from the icon, and a stale-looking date on training that
  // is still current reads as "out of date" when nothing is wrong. The filename
  // is still searchable, just not printed.
  const meta = isUpload ? fmtSize(item.content_size) : '';

  // A file: mint a URL and open it in a new tab. The tab is opened
  // SYNCHRONOUSLY, before the await — after an await the browser stops treating
  // window.open as user-initiated and Safari/Firefox block it silently.
  // `noopener` can't be passed as a window feature (it makes window.open return
  // null and the handle is needed), so the opener is severed by hand.
  const openFile = async (disposition) => {
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setBusy(true);
    try {
      const res = await getFileUrl({ id: item.id, disposition }).unwrap();
      if (res?.url) {
        if (tab) tab.location = res.url;
        else window.location.assign(res.url);   // blocked anyway: fall back in-place
      } else {
        if (tab) tab.close();
        onError('That file could not be opened.');
      }
    } catch (e) {
      if (tab) tab.close();
      onError(e?.data?.detail || 'That file could not be opened.');
    } finally { setBusy(false); }
  };

  const toggleVideo = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (videoUrl) return;
    setBusy(true);
    try {
      const res = await getFileUrl({ id: item.id, disposition: 'inline' }).unwrap();
      if (res?.url) setVideoUrl(res.url);
      else onError('That video could not be loaded.');
    } catch (e) {
      onError(e?.data?.detail || 'That video could not be loaded.');
      setOpen(false);
    } finally { setBusy(false); }
  };

  const label = (
    <>
      <i className={`bi bi-${KIND_ICONS[kind] || fileIcon(item.filename)}`}
        style={{ color: brand.tealBlue, fontSize: '1.15rem', flex: '0 0 auto' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem' }}>
          {item.title}
        </span>
        {item.description && (
          <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.82rem', marginTop: '0.1rem' }}>
            {item.description}
          </span>
        )}
        {meta && (
          <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.15rem' }}>
            {meta}
          </span>
        )}
      </span>
    </>
  );

  const chevron = (icon) => (
    <i className={`bi bi-${busy ? 'hourglass-split' : icon}`}
      style={{ color: brand.charcoal, flex: '0 0 auto' }} />
  );

  // A plain website link: a real anchor, so the browser's own affordances work.
  // A C2P Central course link goes via that site's login page, or the reader
  // lands on a login form that has forgotten which lesson they wanted.
  if (kind === 'LINK') {
    return (
      <a style={rowStyle} href={c2pCentralLoginLink(item.url)} target="_blank" rel="noopener noreferrer">
        {label}
        {chevron('arrow-up-right')}
      </a>
    );
  }

  // A hosted video (YouTube/Vimeo): expands into the provider's player. `src`
  // is the server-built embed URL, never the pasted one.
  if (kind === 'EMBED') {
    return (
      <div style={{ borderTop: `1px solid ${brand.softGray}` }}>
        <button type="button" style={{ ...rowStyle, borderTop: 'none' }}
          onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {label}
          {chevron(open ? 'chevron-up' : 'play-fill')}
        </button>
        {open && item.embed_url && (
          <PlayerFrame>
            <iframe
              src={item.embed_url}
              title={item.title}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </PlayerFrame>
        )}
        {open && !item.embed_url && (
          // Only reachable if a row's embed_url failed the server's outbound
          // check. The link still works, so the row still does something.
          <div style={{ padding: '0 0.25rem 0.7rem' }}>
            <a href={c2pCentralLoginLink(item.url)} target="_blank" rel="noopener noreferrer"
              style={{ color: brand.tealBlue, fontSize: '0.82rem' }}>
              Open this video in a new tab
            </a>
          </div>
        )}
      </div>
    );
  }

  // An uploaded video: expands into an inline player over a presigned URL.
  if (kind === 'VIDEO') {
    return (
      <div style={{ borderTop: `1px solid ${brand.softGray}` }}>
        <button type="button" style={{ ...rowStyle, borderTop: 'none' }}
          onClick={toggleVideo} aria-expanded={open} disabled={busy}>
          {label}
          {chevron(open ? 'chevron-up' : 'play-fill')}
        </button>
        {open && videoUrl && !unplayable && (
          <PlayerFrame>
            {/* No <track>: these are staff screen recordings with no caption
                file to point at.
                Do NOT add a lint-suppression comment for
                jsx-a11y/media-has-caption here. That rule name does not exist
                in the eslint-plugin-jsx-a11y version the production image
                installs, and a suppression naming an unknown rule is itself a
                build error there ("Definition for rule ... was not found") —
                it fails `docker build`, not just `npm test`. The rule is not
                enabled by react-app, so there is nothing to suppress. */}
            <video
              src={videoUrl}
              controls
              preload="metadata"
              onError={() => setUnplayable(true)}
              style={{ width: '100%', height: '100%', background: '#000' }}
            />
          </PlayerFrame>
        )}
        {open && unplayable && (
          <div style={{ padding: '0 0.25rem 0.7rem', color: brand.charcoal, fontSize: '0.82rem' }}>
            <i className="bi bi-info-circle me-1" />
            This browser can&apos;t play {item.filename ? `"${item.filename}"` : 'that format'}.
            <button type="button" onClick={() => openFile('attachment')}
              style={{
                background: 'none', border: 'none', padding: '0 0 0 0.3rem',
                color: brand.tealBlue, fontSize: '0.82rem', cursor: 'pointer',
                textDecoration: 'underline',
              }}>
              Download it instead
            </button>
          </div>
        )}
      </div>
    );
  }

  // A document.
  return (
    <button type="button" style={rowStyle} onClick={() => openFile('inline')} disabled={busy}>
      {label}
      {chevron('box-arrow-up-right')}
    </button>
  );
}
ResourceRow.propTypes = {
  item: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
};

const matches = (q, ...fields) =>
  fields.filter(Boolean).join(' ').toLowerCase().includes(q);

export default function ResourceLibrary() {
  const { slug } = useParams();
  const { data, isLoading } = useGetResourcePagesQuery();
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const allPages = useMemo(() => data?.pages || [], [data]);

  // One page per URL, keyed on the slug. An unknown or absent slug falls back
  // to the first page rather than erroring: the bare /resources path is a live
  // target (the nav group's own header), and a page unpublished since someone
  // bookmarked it should land them somewhere useful rather than on a dead end.
  const page = useMemo(
    () => allPages.find((p) => p.slug === slug) || allPages[0] || null,
    [allPages, slug],
  );
  const unknownSlug = Boolean(slug && page && page.slug !== slug);

  // Search is scoped to the page on screen, matching the marketing view: a
  // cross-page search belongs to a global palette, not to a page that shows one
  // of them — finding a hit you then can't see is worse than not finding it.
  const items = useMemo(() => {
    const rows = page?.items || [];
    if (!query) return rows;
    return rows.filter((r) => matches(query, r.title, r.description, r.filename, r.url));
  }, [page, query]);

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          {/* The page IS the heading. "Resource Library" is the nav group above
              it and doesn't need repeating. */}
          <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {page?.name || 'Resource Library'}
            {/* Only a reader who is seeing this page BECAUSE they hold BETA gets
                the label — the server computes that, so a page everyone can
                already read is never marked pre-release just because the
                authoring box was left ticked. Worth saying out loud: someone in
                the pilot group should know the page may still change, and should
                not forward it to a colleague who cannot open it. */}
            {page?.beta_preview && (
              <span style={{
                background: brand.goldenrod, color: brand.deepBlue, borderRadius: 4,
                padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700,
                letterSpacing: '0.3px', textTransform: 'uppercase',
              }}
              title="Early access — you can see this because you are a beta tester. It is not published to everyone yet.">
                <i className="bi bi-hourglass-split me-1" />Beta preview
              </span>
            )}
          </h4>
          <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
            {page?.description || 'Reference material, guides and recordings.'}
          </p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={page ? `Search ${page.name}…` : 'Search resources…'}
          aria-label="Search this page"
          style={{
            border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '6px 12px',
            fontSize: '0.85rem', color: brand.deepBlue, width: 260, background: 'white',
          }}
        />
      </div>

      {unknownSlug && (
        <div style={{ background: 'rgba(208,155,44,0.12)', border: `1px solid ${brand.goldenrod}`, color: brand.deepBlue, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-info-circle me-1" />
          That page isn&apos;t published any more — showing {page.name} instead.
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.08)', border: `1px solid ${brand.red}`, color: brand.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}

      {isLoading && (
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      )}

      {!isLoading && !page && (
        <div style={cardStyle}>
          <div style={{ padding: '1.25rem', color: brand.charcoal, fontSize: '0.9rem' }}>
            There are no resource pages published yet. The Marketing team add them
            under Admin → Marketing → Resource Library.
          </div>
        </div>
      )}

      {!isLoading && page && (
        <div style={cardStyle}>
          <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.8rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
            {page.icon && <i className={`bi bi-${page.icon}`} style={{ color: brand.goldenrod, fontSize: '0.95rem' }} />}
            <h3 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
              {page.name}
            </h3>
            <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
              {items.length}
            </span>
          </div>
          <div style={{ padding: '0.5rem 1.25rem 1rem' }}>
            <RichText text={page.intro} style={{ marginTop: '0.6rem' }} />

            {items.length === 0 && (
              <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
                {query
                  ? `Nothing in ${page.name} matches “${search}”.`
                  : 'Nothing published on this page yet.'}
              </p>
            )}

            {items.map((item) => (
              <ResourceRow key={item.id} item={item} onError={setError} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
